import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  CalendarDays,
  EyeOff,
  Layers,
  MapPin,
  Monitor,
  Moon,
  Palette,
  Play,
  Rows2,
  Rows4,
  Rss,
  Shield,
  Ship,
  Sun,
  Table2,
  UserRound,
  Volume2,
} from "lucide-react";

import { useUser } from "@/auth/UserContext";
import {
  useTheme,
  type DensityPreference,
  type ThemePreference,
} from "@/context/ThemeContext";
import { usePreferencesUI } from "@/context/PreferencesUIContext";
import { useUserPreferences } from "@/context/UserPreferencesContext";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";
import { SensitiveText } from "@/components/common/Sensitive";
import {
  FEED_GROUP_BY_OPTIONS,
  feedHiddenGroupLabel,
  isFeedHiddenGroupSensitive,
  type FeedGroupBy,
} from "@/features/feed/feedDisplayGroups";
import {
  playTwidoNotificationPing,
  unlockNotificationAudio,
} from "@/lib/notificationSound";
import {
  PreferenceGroup,
  PreferenceReveal,
  PreferenceSection,
  PreferenceSwitch,
} from "./PreferenceControls";
import {
  FEED_PUSH_SEARCH_TEXT,
  FeedPushPreferencesSection,
} from "./FeedPushPreferencesSection";
import { preferenceMatches } from "./preferenceSearch";
import { PreferencesPreview } from "./PreferencesPreview";
import { PreferencesSearch } from "./PreferencesSearch";
import "./preferences.css";

interface PreferencesPanelProps {
  showPreview?: boolean;
}

export function PreferencesPanel({ showPreview = true }: PreferencesPanelProps) {
  const navigate = useNavigate();
  const { closePreferences } = usePreferencesUI();
  const { isAdmin, isSalesPerson, isClient, profile } = useUser();
  const { theme, density, setDensity, preference, setPreference } = useTheme();
  const {
    preferences,
    setShowLocodes,
    setShowCarrierLogos,
    setShowWeekNumbers,
    setHideSensitiveData,
    setScopeToMyBookings,
    setFeedGroupBy,
    restoreFeedGroup,
    clearFeedHiddenGroups,
    setNotificationSounds,
  } = useUserPreferences();
  const [query, setQuery] = useState("");
  const salesLink = profile?.sales_person_link?.trim();
  const consigneeLink = profile?.consignee_link?.trim();
  const hasOpenedBy = (profile?.opened_by_values ?? []).some((v) => v.trim());
  const serverScopedBookings = isSalesPerson || isClient;

  const visibility = useMemo(() => {
    const appearanceTitle = preferenceMatches(
      query,
      "Appearance",
      "Theme, density, and how the workspace is laid out.",
    );
    const themeHit =
      appearanceTitle ||
      preferenceMatches(
        query,
        "Theme",
        "Light dark system appearance device",
        "Light, dark, or follow the device appearance setting.",
      );
    const densityHit =
      appearanceTitle ||
      preferenceMatches(
        query,
        "Content density",
        "Comfortable compact spacing tables lists rows",
        "Reduced spacing gives more rows visible in tables and lists.",
      );

    const notificationsTitle = preferenceMatches(
      query,
      "Notifications",
      "Sounds and push alerts for live feed activity.",
    );
    const soundsHit =
      notificationsTitle ||
      preferenceMatches(
        query,
        "Notification sounds",
        "Play sample ping audio volume",
        "A quiet neon ping for new live feed events in the current tab.",
      );
    const pushHit =
      notificationsTitle || preferenceMatches(query, FEED_PUSH_SEARCH_TEXT);

    const displayTitle = preferenceMatches(
      query,
      "Data display",
      "Control how ports, carriers, and dates appear across the app.",
    );
    const locodesHit =
      displayTitle ||
      preferenceMatches(
        query,
        "Show UN/LOCODEs",
        "unlocode locode ports routes Shanghai CNSHA",
        'Display locodes in routes and port labels (e.g. "Shanghai (CNSHA)").',
      );
    const logosHit =
      displayTitle ||
      preferenceMatches(
        query,
        "Show carrier logos",
        "carrier logo brand",
        "Show carrier logos beside names in tables and detail views.",
      );
    const weeksHit =
      displayTitle ||
      preferenceMatches(
        query,
        "Show week numbers",
        "iso week date W11",
        'Append ISO week labels to dates (e.g. "Mar 14, 2026 · W11-2026").',
      );
    const privacyHit =
      displayTitle ||
      preferenceMatches(
        query,
        "Privacy masking",
        "hide sensitive blur names emails pricing contracts",
        "Blur names, emails, organization, pricing, and contract references in the feed, chrome, tables, and detail views.",
      );

    const activityTitle = preferenceMatches(
      query,
      "Bookings & activity",
      "Default booking scope and how the activity feed is grouped.",
    );
    const scopeHit =
      activityTitle ||
      preferenceMatches(
        query,
        isClient
          ? "Show only linked consignee"
          : isSalesPerson
            ? "Show only linked bookings"
            : "Show only my bookings by default",
        "booking scope sales opener consignee client portal filter friday",
        isClient ? consigneeLink : salesLink,
      );
    const groupByHit =
      activityTitle ||
      preferenceMatches(
        query,
        "Group by",
        FEED_GROUP_BY_OPTIONS.map((option) => option.label).join(" "),
        "On My Feed, group by carrier, agent, booking, or other entities to scan activity by portfolio. Default is by date.",
      );
    const hiddenGroupsHit =
      preferences.feedHiddenGroups.length > 0 &&
      (activityTitle ||
        preferenceMatches(
          query,
          "Hidden feed groups",
          "restore hidden",
          preferences.feedHiddenGroups.map(feedHiddenGroupLabel).join(" "),
        ));

    const organizationHit =
      isAdmin &&
      preferenceMatches(
        query,
        "Organization",
        "admin administration team members recommendation rules scheduled reports",
        "Manage team members, recommendation rules, and scheduled reports.",
      );

    const previewHit =
      showPreview &&
      preferenceMatches(
        query,
        "Live preview",
        "sample table shipper route contract savings",
        "Changes apply instantly across the app — no reload needed.",
      );

    return {
      appearance: themeHit || densityHit,
      theme: themeHit,
      density: densityHit,
      notifications: soundsHit || pushHit,
      notificationsTitle,
      sounds: soundsHit,
      display: locodesHit || logosHit || weeksHit || privacyHit,
      locodes: locodesHit,
      logos: logosHit,
      weeks: weeksHit,
      privacy: privacyHit,
      activity: scopeHit || groupByHit || hiddenGroupsHit,
      scope: scopeHit,
      groupBy: groupByHit,
      hiddenGroups: hiddenGroupsHit,
      organization: organizationHit,
      preview: previewHit,
    };
  }, [
    isAdmin,
    isSalesPerson,
    isClient,
    preferences.feedHiddenGroups,
    query,
    salesLink,
    consigneeLink,
    showPreview,
  ]);

  const hasResults =
    visibility.appearance ||
    visibility.notifications ||
    visibility.display ||
    visibility.activity ||
    visibility.organization ||
    visibility.preview;

  const scopeDescription = isClient
    ? consigneeLink
      ? `Client portal accounts always see shipments for consignee ${consigneeLink}. This cannot be turned off.`
      : "Client portal accounts are scoped to a consignee link. Ask an admin to set your consignee link."
    : isSalesPerson
      ? salesLink
        ? `Sales accounts always see bookings linked to you as sales (${salesLink}) or as the opener. This cannot be turned off.`
        : "Sales accounts always see bookings linked to you as sales or as the opener. Ask an admin to set your sales-person link."
      : salesLink || hasOpenedBy
        ? "Filter bookings, home charts, and metrics to bookings linked to you as sales or as the opener (either match). You can still show all bookings from the list. Automatically off on Fridays."
        : "Filter bookings, home charts, and metrics to bookings linked to you as sales or as the opener when your account has those assignments. Automatically off on Fridays.";

  return (
    <div className="space-y-4">
      <div className="sticky top-0 z-10 bg-background/80 pb-1 backdrop-blur-sm">
        <PreferencesSearch value={query} onChange={setQuery} />
      </div>

      <div className="pref-list pref-list--sections">
        <PreferenceReveal visible={visibility.appearance}>
          <PreferenceSection
            icon={Palette}
            title="Appearance"
            description="Theme, density, and how the workspace is laid out."
          >
            <PreferenceReveal visible={visibility.theme}>
              <PreferenceGroup
                title="Theme"
                description="Light, dark, or follow the device appearance setting."
                icon={Sun}
              >
                <ToggleGroup
                  type="single"
                  value={preference}
                  onValueChange={(value) => {
                    if (value) setPreference(value as ThemePreference);
                  }}
                  className="glass-control-track w-full justify-stretch"
                >
                  <ToggleGroupItem value="light" className="flex-1">
                    <Sun />
                    Light
                  </ToggleGroupItem>
                  <ToggleGroupItem value="dark" className="flex-1">
                    <Moon />
                    Dark
                  </ToggleGroupItem>
                  <ToggleGroupItem value="system" className="flex-1">
                    <Monitor />
                    System
                  </ToggleGroupItem>
                </ToggleGroup>
              </PreferenceGroup>
            </PreferenceReveal>
            <PreferenceReveal visible={visibility.density}>
              <PreferenceGroup
                title="Content density"
                description="Reduced spacing gives more rows visible in tables and lists."
                icon={Rows4}
              >
                <ToggleGroup
                  type="single"
                  value={density}
                  onValueChange={(value) => {
                    if (value) setDensity(value as DensityPreference);
                  }}
                  className="glass-control-track w-full justify-stretch"
                >
                  <ToggleGroupItem value="comfortable" className="flex-1">
                    <Rows2 />
                    Comfortable
                  </ToggleGroupItem>
                  <ToggleGroupItem value="compact" className="flex-1">
                    <Rows4 />
                    Compact
                  </ToggleGroupItem>
                </ToggleGroup>
              </PreferenceGroup>
            </PreferenceReveal>
          </PreferenceSection>
        </PreferenceReveal>

        <PreferenceReveal visible={visibility.notifications}>
          <PreferenceSection
            icon={Bell}
            title="Notifications"
            description="Sounds and push alerts for live feed activity."
          >
            <PreferenceReveal visible={visibility.sounds}>
              <PreferenceGroup
                title="In-app sounds"
                description="Play a short sound when new activity arrives while Twido is open."
                icon={Volume2}
              >
                <div className="space-y-3">
                  <PreferenceSwitch
                    id="pref-notification-sounds"
                    icon={Volume2}
                    label="Notification sounds"
                    description="A quiet neon ping for new live feed events in the current tab."
                    checked={preferences.notificationSounds}
                    onCheckedChange={setNotificationSounds}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-fit"
                    onClick={() => {
                      unlockNotificationAudio();
                      playTwidoNotificationPing(theme);
                    }}
                  >
                    <Play />
                    Play sample
                  </Button>
                </div>
              </PreferenceGroup>
            </PreferenceReveal>
            <FeedPushPreferencesSection
              query={query}
              sectionMatched={visibility.notificationsTitle}
            />
          </PreferenceSection>
        </PreferenceReveal>

        <PreferenceReveal visible={visibility.display}>
          <PreferenceSection
            icon={MapPin}
            title="Data display"
            description="Control how ports, carriers, and dates appear across the app."
          >
            <PreferenceReveal visible={visibility.locodes}>
              <PreferenceSwitch
                id="pref-locodes"
                icon={MapPin}
                label="Show UN/LOCODEs"
                description='Display locodes in routes and port labels (e.g. "Shanghai (CNSHA)").'
                checked={preferences.showLocodes}
                onCheckedChange={setShowLocodes}
              />
            </PreferenceReveal>
            <PreferenceReveal visible={visibility.logos}>
              <PreferenceSwitch
                id="pref-carrier-logos"
                icon={Ship}
                label="Show carrier logos"
                description="Show carrier logos beside names in tables and detail views."
                checked={preferences.showCarrierLogos}
                onCheckedChange={setShowCarrierLogos}
              />
            </PreferenceReveal>
            <PreferenceReveal visible={visibility.weeks}>
              <PreferenceSwitch
                id="pref-week-numbers"
                icon={CalendarDays}
                label="Show week numbers"
                description='Append ISO week labels to dates (e.g. "Mar 14, 2026 · W11-2026").'
                checked={preferences.showWeekNumbers}
                onCheckedChange={setShowWeekNumbers}
              />
            </PreferenceReveal>
            <PreferenceReveal visible={visibility.privacy}>
              <PreferenceSwitch
                id="pref-privacy-masking"
                icon={EyeOff}
                label="Privacy masking"
                description="Blur names, emails, organization, pricing, and contract references in the feed, chrome, tables, and detail views."
                checked={preferences.hideSensitiveData}
                onCheckedChange={setHideSensitiveData}
              />
            </PreferenceReveal>
          </PreferenceSection>
        </PreferenceReveal>

        <PreferenceReveal visible={visibility.activity}>
          <PreferenceSection
            icon={Rss}
            title="Bookings & activity"
            description="Default booking scope and how the activity feed is grouped."
          >
            <PreferenceReveal visible={visibility.scope}>
              {serverScopedBookings ? (
                <PreferenceSwitch
                  id="pref-scope-my-bookings"
                  icon={UserRound}
                  label={
                    isClient
                      ? "Show only linked consignee"
                      : "Show only linked bookings"
                  }
                  description={scopeDescription}
                  checked
                  disabled
                  lockedLabel="Locked"
                  onCheckedChange={() => {}}
                />
              ) : (
                <PreferenceSwitch
                  id="pref-scope-my-bookings"
                  icon={UserRound}
                  label="Show only my bookings by default"
                  description={scopeDescription}
                  checked={preferences.scopeToMyBookings}
                  onCheckedChange={setScopeToMyBookings}
                />
              )}
            </PreferenceReveal>
            <PreferenceReveal visible={visibility.groupBy}>
              <div className="space-y-1.5">
                <label
                  htmlFor="pref-feed-group-by"
                  className="flex items-center gap-2 text-sm font-medium"
                >
                  <Layers className="size-4 shrink-0 text-muted-foreground" />
                  Group by
                </label>
                <Select
                  id="pref-feed-group-by"
                  value={preferences.feedGroupBy}
                  onChange={(next) => setFeedGroupBy(next as FeedGroupBy)}
                  options={FEED_GROUP_BY_OPTIONS.map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                />
                <p className="text-xs text-muted-foreground">
                  On My Feed, group by carrier, agent, booking, or other entities
                  to scan activity by portfolio. Default is by date.
                </p>
              </div>
            </PreferenceReveal>
            <PreferenceReveal visible={visibility.hiddenGroups}>
              <div className="space-y-2 rounded-lg border border-border/60 bg-muted/20 p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="flex items-center gap-2 text-sm font-medium">
                    <EyeOff className="size-4 shrink-0 text-muted-foreground" />
                    Hidden feed groups
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={clearFeedHiddenGroups}
                  >
                    Restore all
                  </Button>
                </div>
                <ul className="space-y-1.5">
                  {preferences.feedHiddenGroups.map((sectionKey) => (
                    <li
                      key={sectionKey}
                      className="flex items-center justify-between gap-2 text-xs"
                    >
                      <span className="min-w-0 truncate text-muted-foreground">
                        {isFeedHiddenGroupSensitive(sectionKey) ? (
                          <SensitiveText value={feedHiddenGroupLabel(sectionKey)} />
                        ) : (
                          feedHiddenGroupLabel(sectionKey)
                        )}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 shrink-0 px-2 text-xs"
                        onClick={() => restoreFeedGroup(sectionKey)}
                      >
                        Restore
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            </PreferenceReveal>
          </PreferenceSection>
        </PreferenceReveal>

        {isAdmin && (
          <PreferenceReveal visible={visibility.organization}>
            <PreferenceSection
              icon={Shield}
              title="Organization"
              description="Manage team members, recommendation rules, and scheduled reports."
            >
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  closePreferences();
                  navigate("/admin");
                }}
              >
                <Shield />
                Open administration
              </Button>
            </PreferenceSection>
          </PreferenceReveal>
        )}

        {showPreview && (
          <PreferenceReveal visible={visibility.preview}>
            <PreferenceSection
              icon={Table2}
              title="Live preview"
              description="Changes apply instantly across the app — no reload needed."
            >
              <PreferencesPreview />
            </PreferenceSection>
          </PreferenceReveal>
        )}

        <PreferenceReveal visible={!hasResults}>
          <p className="py-6 text-center text-sm text-muted-foreground">
            No preferences match “{query.trim()}”
          </p>
        </PreferenceReveal>
      </div>
    </div>
  );
}
